import React, { useEffect } from 'react';
import { Platform, View } from 'react-native';
import { WebView } from 'react-native-webview';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Sheet, SheetContent } from '@/components/ui/sheet';

/** O ponto que o prestador marcou, já com o endereço que o mapa conhece. */
export interface PickedPlace {
  latitude: number;
  longitude: number;
  /** O endereço do ponto, do jeito que o mapa devolve. Pode vir vazio. */
  address: string;
}

interface Props {
  open: boolean;
  /** Onde o mapa abre: o ponto já marcado, ou o GPS, ou o centro do país. */
  latitude?: number | null;
  longitude?: number | null;
  /** O que já dá para procurar sozinho: o endereço montado até aqui. */
  query?: string;
  onPick: (place: PickedPlace) => void;
  onClose: () => void;
}

const DEFAULT_CENTER = { lat: -15.79, lon: -47.88, zoom: 4 };

/**
 * O mapa para marcar o ponto exato.
 *
 * **Sem chave de API e sem SDK nativo de mapa**: é Leaflet sobre OpenStreetMap
 * dentro de um WebView, com busca e endereço reverso no Nominatim. A
 * alternativa era o SDK do Google — que no Android exige chave, faturamento e
 * um mapa em branco para quem esquecer de configurar. Para "arrasta o alfinete
 * até a porta da loja", isto entrega o mesmo.
 *
 * A busca aceita nome de estabelecimento ("barbearia do zé, contagem"), que é
 * como o prestador procura o próprio negócio.
 */
export function MapPickerSheet({
  open,
  latitude,
  longitude,
  query,
  onPick,
  onClose,
}: Props) {
  const center =
    latitude != null && longitude != null
      ? { lat: latitude, lon: longitude, zoom: 17 }
      : DEFAULT_CENTER;

  return (
    <Sheet
      open={open}
      onOpenChange={next => {
        if (!next) onClose();
      }}
      side="bottom"
    >
      <SheetContent className="h-[85%] gap-3">
        <View className="gap-1">
          <ThemedText className="text-lg font-bold text-foreground">
            Marque o ponto exato
          </ThemedText>
          <ThemedText className="text-xs text-foreground/45">
            Procure pelo nome do seu negócio ou arraste o alfinete até a porta.
          </ThemedText>
        </View>

        <View className="flex-1 overflow-hidden rounded-2xl">
          {Platform.OS === 'web' ? (
            <WebMap html={mapHtml(center, query ?? '')} onPick={onPick} />
          ) : (
          <WebView
            originWhitelist={['*']}
            source={{ html: mapHtml(center, query ?? '') }}
            onMessage={event => {
              try {
                const place = JSON.parse(
                  event.nativeEvent.data,
                ) as PickedPlace;
                if (
                  typeof place.latitude === 'number' &&
                  typeof place.longitude === 'number'
                ) {
                  onPick(place);
                }
              } catch {
                // Mensagem que não é ponto não interessa: o sheet continua.
              }
            }}
            // O mapa é a tela inteira do sheet; rolagem do WebView atrapalharia
            // o arrasto do alfinete.
            scrollEnabled={false}
            javaScriptEnabled
            domStorageEnabled
            geolocationEnabled
          />
          )}
        </View>

        <PressableScale onPress={onClose} className="items-center py-1">
          <ThemedText className="text-xs font-semibold text-foreground/45">
            Cancelar
          </ThemedText>
        </PressableScale>
      </SheetContent>
    </Sheet>
  );
}

/**
 * O mesmo mapa na web, num `iframe`.
 *
 * `react-native-webview` não tem implementação para web — sem isto o cartão
 * de endereço quebrava justamente onde o produto é testado primeiro. O
 * `iframe` roda o mesmo HTML e devolve o ponto por `postMessage`.
 */
function WebMap({
  html,
  onPick,
}: {
  html: string;
  onPick: (place: PickedPlace) => void;
}) {
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      try {
        const place = JSON.parse(String(event.data)) as PickedPlace;
        if (
          typeof place.latitude === 'number' &&
          typeof place.longitude === 'number'
        ) {
          onPick(place);
        }
      } catch {
        // Mensagem de outra origem (extensão, devtools) não interessa.
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [onPick]);

  return React.createElement('iframe', {
    srcDoc: html,
    style: { border: 0, width: '100%', height: '100%' },
    allow: 'geolocation',
  });
}

/**
 * A página do mapa.
 *
 * Mora como string aqui de propósito: é a única parte do app que roda fora do
 * React Native, e um arquivo `.html` solto no bundle se perderia do componente
 * que o consome.
 */
function mapHtml(
  center: { lat: number; lon: number; zoom: number },
  query: string,
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>
  html, body, #map { height: 100%; margin: 0; padding: 0; font-family: -apple-system, Roboto, sans-serif; }
  #bar { position: absolute; z-index: 500; top: 10px; left: 10px; right: 10px; display: flex; gap: 6px; }
  #q { flex: 1; border: 0; border-radius: 12px; padding: 12px; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,.2); }
  #go { border: 0; border-radius: 12px; padding: 12px 14px; background: #FD5001; color: #fff; font-weight: 600; }
  #results { position: absolute; z-index: 500; top: 58px; left: 10px; right: 10px; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,.2); }
  #results div { padding: 10px 12px; font-size: 13px; border-bottom: 1px solid #eee; }
  #confirm { position: absolute; z-index: 500; bottom: 16px; left: 16px; right: 16px; border: 0; border-radius: 999px; padding: 14px; background: #FD5001; color: #fff; font-size: 15px; font-weight: 700; }
  #hint { position: absolute; z-index: 500; bottom: 66px; left: 16px; right: 16px; background: rgba(255,255,255,.94); border-radius: 12px; padding: 8px 12px; font-size: 12px; color: #444; }
</style>
</head>
<body>
<div id="bar">
  <input id="q" placeholder="Nome do lugar, rua, cidade" value="${escapeHtml(query)}" />
  <button id="go">Buscar</button>
</div>
<div id="results" style="display:none"></div>
<div id="hint">Arraste o alfinete até o ponto exato.</div>
<button id="confirm">Usar este ponto</button>
<div id="map"></div>
<script>
  var map = L.map('map').setView([${center.lat}, ${center.lon}], ${center.zoom});
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '© OpenStreetMap'
  }).addTo(map);

  var marker = L.marker([${center.lat}, ${center.lon}], { draggable: true }).addTo(map);
  map.on('click', function (event) { marker.setLatLng(event.latlng); });

  var results = document.getElementById('results');
  var hint = document.getElementById('hint');

  function search() {
    var term = document.getElementById('q').value.trim();
    if (!term) return;
    hint.textContent = 'Procurando...';
    fetch('https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=br&limit=5&q=' + encodeURIComponent(term))
      .then(function (response) { return response.json(); })
      .then(function (list) {
        hint.textContent = list.length ? 'Toque no resultado certo.' : 'Não achei esse lugar. Arraste o alfinete.';
        results.innerHTML = '';
        results.style.display = list.length ? 'block' : 'none';
        list.forEach(function (place) {
          var row = document.createElement('div');
          row.textContent = place.display_name;
          row.onclick = function () {
            var point = [parseFloat(place.lat), parseFloat(place.lon)];
            marker.setLatLng(point);
            map.setView(point, 18);
            results.style.display = 'none';
            hint.textContent = 'Confira o alfinete e confirme.';
          };
          results.appendChild(row);
        });
      })
      .catch(function () { hint.textContent = 'Não consegui buscar agora. Arraste o alfinete.'; });
  }

  document.getElementById('go').onclick = search;
  document.getElementById('q').addEventListener('keydown', function (event) {
    if (event.key === 'Enter') search();
  });

  document.getElementById('confirm').onclick = function () {
    var point = marker.getLatLng();
    hint.textContent = 'Confirmando...';
    fetch('https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + point.lat + '&lon=' + point.lng)
      .then(function (response) { return response.json(); })
      .then(function (place) { send(point, place && place.display_name ? place.display_name : ''); })
      // Endereço reverso é enfeite: o ponto sozinho já resolve chegar lá.
      .catch(function () { send(point, ''); });
  };

  function send(point, address) {
    var payload = JSON.stringify({
      latitude: point.lat,
      longitude: point.lng,
      address: address
    });
    // Celular fala pelo WebView; na web, o iframe fala com quem o abriu.
    if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(payload);
    else if (window.parent) window.parent.postMessage(payload, '*');
  }
</script>
</body>
</html>`;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
